import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { Edit, Plus, Search, Tag } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const MODULOS = [
  { value: "pedido", label: "Pedidos" },
  { value: "produto", label: "Produtos" },
  { value: "cliente", label: "Clientes" },
  { value: "orcamento", label: "Orçamentos" },
] as const;

function nomesModulos(modulos: string[]) {
  return modulos.map((modulo) => MODULOS.find((item) => item.value === modulo)?.label || modulo).join(", ");
}

export default function CadastroEtiquetas() {
  const utils = trpc.useUtils();
  const [busca, setBusca] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [emitenteId, setEmitenteId] = useState("");
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [modulos, setModulos] = useState<string[]>([]);
  const [cor, setCor] = useState("#2563eb");

  const { data: etiquetas = [], isLoading } = trpc.etiquetas.list.useQuery({ search: busca || undefined, incluirInativas: true });
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();

  const emitentesPorId = useMemo(() => new Map(emitentes.map((emitente: any) => [emitente.id, emitente])), [emitentes]);

  const fecharDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setEmitenteId("");
    setNome("");
    setDescricao("");
    setModulos([]);
    setCor("#2563eb");
  };

  const abrirNovo = () => {
    fecharDialog();
    setDialogOpen(true);
  };

  const abrirEdicao = (etiqueta: any) => {
    setEditing(etiqueta);
    setEmitenteId(String(etiqueta.emitenteId));
    setNome(etiqueta.nome || "");
    setDescricao(etiqueta.descricao || "");
    setModulos(etiqueta.modulos || []);
    setCor(etiqueta.cor || "#2563eb");
    setDialogOpen(true);
  };

  const invalidar = async () => {
    await utils.etiquetas.list.invalidate();
  };

  const createMutation = trpc.etiquetas.create.useMutation({
    onSuccess: async () => {
      await invalidar();
      toast.success("Etiqueta cadastrada.");
      fecharDialog();
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = trpc.etiquetas.update.useMutation({
    onSuccess: async () => {
      await invalidar();
      toast.success("Etiqueta atualizada.");
      fecharDialog();
    },
    onError: (error) => toast.error(error.message),
  });

  const setAtivaMutation = trpc.etiquetas.setAtiva.useMutation({
    onSuccess: async () => {
      await invalidar();
      toast.success("Situação da etiqueta atualizada.");
    },
    onError: (error) => toast.error(error.message),
  });

  const salvar = () => {
    if (!emitenteId) return toast.error("Selecione o emitente.");
    if (!nome.trim()) return toast.error("Informe o nome da etiqueta.");
    if (!modulos.length) return toast.error("Selecione pelo menos um módulo.");
    const payload = { nome: nome.trim(), descricao: descricao.trim() || null, modulos, cor };
    if (editing) updateMutation.mutate({ id: editing.id, ...payload });
    else createMutation.mutate({ emitenteId: Number(emitenteId), ...payload });
  };

  const alternarModulo = (modulo: string) => {
    setModulos((anteriores) => anteriores.includes(modulo)
      ? anteriores.filter((item) => item !== modulo)
      : [...anteriores, modulo]);
  };

  return (
    <div className="container mx-auto space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Cadastro de Etiquetas</h1>
          <p className="text-sm text-muted-foreground">Etiquetas disponíveis para pedidos, produtos, clientes e orçamentos.</p>
        </div>
        <Button onClick={abrirNovo}><Plus className="mr-2 h-4 w-4" />Nova Etiqueta</Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="Buscar por ID, emitente, nome ou descrição..."
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">ID</TableHead>
                <TableHead>Emitente</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Módulos disponíveis</TableHead>
                <TableHead>Cor</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead className="w-24">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : etiquetas.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">Nenhuma etiqueta encontrada.</TableCell></TableRow>
              ) : etiquetas.map((etiqueta: any) => (
                <TableRow key={etiqueta.id} className={etiqueta.ativo ? "" : "opacity-60"}>
                  <TableCell>#{etiqueta.id}</TableCell>
                  <TableCell>{etiqueta.emitenteNome || emitentesPorId.get(etiqueta.emitenteId)?.razaoSocial || "-"}</TableCell>
                  <TableCell className="font-medium">{etiqueta.nome}</TableCell>
                  <TableCell>{etiqueta.descricao || "-"}</TableCell>
                  <TableCell>{nomesModulos(etiqueta.modulos || [])}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-full border" style={{ backgroundColor: etiqueta.cor }} />{etiqueta.cor}</span>
                  </TableCell>
                  <TableCell>{etiqueta.ativo ? "Ativa" : "Inativa"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" title="Editar" onClick={() => abrirEdicao(etiqueta)}><Edit className="h-4 w-4" /></Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title={etiqueta.ativo ? "Desativar" : "Ativar"}
                        onClick={() => setAtivaMutation.mutate({ id: etiqueta.id, ativo: !etiqueta.ativo })}
                      >
                        <Tag className={`h-4 w-4 ${etiqueta.ativo ? "text-green-600" : "text-muted-foreground"}`} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) fecharDialog(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar Etiqueta" : "Nova Etiqueta"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Emitente *</Label>
              <Select value={emitenteId} onValueChange={setEmitenteId} disabled={!!editing}>
                <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                <SelectContent>
                  {emitentes.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.razaoSocial}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome *</Label>
              <Input value={nome} maxLength={120} onChange={(event) => setNome(event.target.value)} />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={descricao} maxLength={2000} rows={3} onChange={(event) => setDescricao(event.target.value)} />
            </div>
            <div>
              <Label>Módulos disponíveis *</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {MODULOS.map((modulo) => (
                  <label key={modulo.value} className="flex items-center gap-2 rounded border p-2 text-sm">
                    <Checkbox checked={modulos.includes(modulo.value)} onCheckedChange={() => alternarModulo(modulo.value)} />
                    {modulo.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Cor *</Label>
              <div className="mt-1 flex items-center gap-3">
                <Input type="color" value={cor} onChange={(event) => setCor(event.target.value)} className="h-10 w-16 p-1" />
                <Input value={cor} maxLength={16} onChange={(event) => setCor(event.target.value)} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={fecharDialog}>Cancelar</Button>
              <Button onClick={salvar} disabled={createMutation.isPending || updateMutation.isPending}>{editing ? "Salvar" : "Cadastrar"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
